/**
 * כל שגיאה מהשרת מתורגמת להודעה בעברית שאומרת מה קרה ומה לעשות.
 * מסמך התכנון, סעיף 4.4.
 */

const POSTGRES_ERRORS: Record<string, string> = {
  '42501': 'אין לך הרשאה לפעולה הזו.',
  '23505': 'הפריט כבר קיים.',
  '23505_unique': 'הפריט כבר קיים.',
  '23503': 'לא ניתן למחוק, יש פריטים שמקושרים לזה.',
  '23502': 'חסר שדה חובה.',
  '22P02': 'אחד הערכים לא בפורמט הנכון.',
  PGRST301: 'ההתחברות פגה. יש להיכנס מחדש.',
  PGRST116: 'לא נמצא פריט מתאים.',
};

const AUTH_ERRORS: Record<string, string> = {
  otp_expired: 'הקוד פג תוקף. בקש/י קוד חדש.',
  invalid_otp: 'הקוד לא נכון. נסה/י שוב.',
  otp_disabled: 'הכניסה בקוד לא זמינה כרגע.',
  over_sms_send_rate_limit: 'נשלחו יותר מדי בקשות. נסה/י שוב בעוד שעה.',
  over_request_rate_limit: 'נשלחו יותר מדי בקשות. נסה/י שוב בעוד כמה דקות.',
  signup_disabled: 'המספר לא רשום במערכת. פנה/י למנהל הסניף.',
  user_banned: 'המספר לא רשום במערכת. פנה/י למנהל הסניף.',
  phone_provider_disabled: 'הכניסה ב SMS לא מוגדרת עדיין. פנה/י למנהל המערכת.',
};

export const GENERIC_ERROR = 'משהו השתבש. נסו שוב.';

/** מספר לא רשום, עובד מושבת, או כל חסימה בנעילה הכפולה. אותה הודעה בדיוק (PRD 2.6). */
export const UNKNOWN_PHONE_ERROR = 'המספר לא רשום במערכת. פנה/י למנהל הסניף.';

type MaybeError = {
  code?: string | null;
  message?: string | null;
  status?: number | null;
  error_code?: string | null;
} | null;

/**
 * הודעה שה RPC זרק במפורש ל RAISE EXCEPTION.
 *
 * הכלל פשוט: מה שכתוב בעברית נכתב כדי שהעובד יקרא אותו ("נשארו 2
 * פריטים שלא סומנו"), ומה שכתוב באנגלית הוא הודעה פנימית למפתח
 * ("invalid attempt") שאין טעם להציג. בלי ההבחנה הזו כל ההודעות
 * המדויקות שנכתבו בשרת היו הופכות ל"משהו השתבש".
 *
 * הכלל חל על כל מקור ולא רק על P0001, כי לא רק RPC כותבות בעברית:
 * hook_before_user_created מחזירה "המספר לא רשום במערכת" עם קוד
 * 403, וההודעה הזו נדרסה ב"אין לך הרשאה לפעולה הזו" — כלומר מי
 * שהמספר שלו לא רשום קיבל בדיוק את ההודעה שלא עוזרת לו.
 *
 * אבל קוד שיש לו מיפוי מפורש גובר: ל 23505 יש נוסח אחיד משלנו,
 * וההודעה שפוסטגרס מייצרת לו עלולה להכיל עברית מתוך שם או ערך.
 */
const HEBREW = /[֐-׿]/;

function raisedMessage(err: MaybeError): string | null {
  const message = err?.message?.trim() ?? '';
  return HEBREW.test(message) ? message : null;
}

export function errorMessage(error: unknown): string {
  const err = error as MaybeError;
  if (!err) return GENERIC_ERROR;

  const code = err.error_code ?? err.code ?? '';
  if (code && AUTH_ERRORS[code]) return AUTH_ERRORS[code];
  if (code && POSTGRES_ERRORS[code]) return POSTGRES_ERRORS[code];

  // אחרי המיפויים המפורשים ולפני נפילה לפי status, אחרת הודעה
  // מדויקת מהשרת מוחלפת בהודעה כללית לפי קוד HTTP
  const raised = raisedMessage(err);
  if (raised) return raised;

  if (err.status === 429) return AUTH_ERRORS.over_request_rate_limit;
  if (err.status === 403 || err.status === 401) return POSTGRES_ERRORS['42501'];

  if (import.meta.env.DEV) {
    console.error('שגיאה לא מוכרת', error);
  }
  return GENERIC_ERROR;
}

/** האם השגיאה היא "המספר לא מורשה", כדי להציג את ההודעה האחידה. */
export function isUnauthorizedPhone(error: unknown): boolean {
  const err = error as MaybeError;
  const code = err?.error_code ?? err?.code ?? '';
  const message = err?.message ?? '';
  return (
    code === 'signup_disabled' ||
    code === 'user_banned' ||
    /not allowed|not authorized|unauthorized phone|no active employee/i.test(message) ||
    // מה ש hook_before_user_created מחזירה בפועל
    message.includes('לא רשום במערכת')
  );
}
